import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Chip, LinearProgress, Stack, Tooltip, Typography } from "@mui/material";
import { Zap, BookOpen, CheckSquare, Bug, CornerDownRight, Circle, ChevronsUp, ChevronUp, Equal, ChevronDown, ChevronsDown } from "lucide-react";
import { T, TYPE, PersonAvatar } from "../user/complaints/shared";
import { ColorChip, SmallLabel } from "../helpdesk/ui";
import { usePm } from "./PmContext";

export { ColorChip, SmallLabel };

// Visual pieces shared by the PM screens. Colours come from configuration.

const TYPE_ICON = { epic: Zap, story: BookOpen, task: CheckSquare, bug: Bug, subtask: CornerDownRight };

export const TypeIcon = ({ typeId, size = 16 }) => {
  const { typeById } = usePm();
  const t = typeById.get(String(typeId));
  const Icon = TYPE_ICON[t?.icon] || TYPE_ICON[t?.key] || Circle;
  return (
    <Tooltip title={t?.name || "Issue"}>
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size + 4, height: size + 4, borderRadius: "5px", bgcolor: t?.color || T.inkMuted, color: "#fff", flexShrink: 0 }}>
        <Icon size={size - 4} strokeWidth={2.5} />
      </Box>
    </Tooltip>
  );
};

const PRIORITY_ICON = { 1: ChevronsDown, 2: ChevronDown, 3: Equal, 4: ChevronUp, 5: ChevronsUp };
export const PriorityIcon = ({ priorityId, size = 16, withLabel = false }) => {
  const { priorityById } = usePm();
  const p = priorityById.get(String(priorityId));
  if (!p) return null;
  const Icon = PRIORITY_ICON[Math.max(1, Math.min(5, p.level))] || Equal;
  const icon = <Icon size={size} color={p.color} strokeWidth={2.5} />;
  return withLabel ? (
    <Stack direction="row" spacing={0.5} alignItems="center">{icon}<Typography sx={{ fontSize: TYPE.small }}>{p.name}</Typography></Stack>
  ) : (
    <Tooltip title={p.name}><Box component="span" sx={{ display: "inline-flex" }}>{icon}</Box></Tooltip>
  );
};

export const StatusPill = ({ statusId }) => {
  const { statusById } = usePm();
  const s = statusById.get(String(statusId));
  return <ColorChip label={s?.name} color={s?.color} />;
};

export const CATEGORY_TONE = { todo: "#98A2B3", in_progress: "#2E90FA", done: "#12B76A" };

export const IssueKey = ({ issue, projectKey, sx, onClick }) => {
  if (onClick) {
    return (
      <Typography
        component="span"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onClick(issue.key, issue);
        }}
        sx={{
          fontSize: TYPE.small,
          fontWeight: 600,
          color: T.inkSecondary,
          textDecoration: issue.statusCategory === "done" ? "line-through" : "none",
          whiteSpace: "nowrap",
          cursor: "pointer",
          "&:hover": { color: T.brand, textDecoration: "underline" },
          ...sx,
        }}
      >
        {issue.key}
      </Typography>
    );
  }
  return (
    <Typography
      component={RouterLink}
      to={`/projects/${projectKey || issue.key.split("-")[0]}/issues/${issue.key}`}
      onClick={(e) => e.stopPropagation()}
      sx={{
        fontSize: TYPE.small,
        fontWeight: 600,
        color: T.inkSecondary,
        textDecoration: issue.statusCategory === "done" ? "line-through" : "none",
        whiteSpace: "nowrap",
        "&:hover": { color: T.brand },
        ...sx,
      }}
    >
      {issue.key}
    </Typography>
  );
};

export const Assignee = ({ person, size = 24, showName = false }) => {
  if (!person?.id) {
    return (
      <Tooltip title="Unassigned">
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: size, height: size, borderRadius: "50%", border: `1.5px dashed ${T.borderStrong}`, flexShrink: 0 }} />
          {showName && <Typography sx={{ fontSize: TYPE.small, color: T.inkMuted }}>Unassigned</Typography>}
        </Stack>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={person.name}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        <PersonAvatar name={person.name} size={size} />
        {showName && <Typography noWrap sx={{ fontSize: TYPE.small }}>{person.name}</Typography>}
      </Stack>
    </Tooltip>
  );
};

export const Labels = ({ labels = [], max = 3 }) => (
  <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 0.5 }}>
    {labels.slice(0, max).map((l) => (
      <Chip key={l} label={l} size="small" sx={{ height: 20, fontSize: "0.68rem", bgcolor: T.track, color: T.inkSecondary }} />
    ))}
    {labels.length > max && <Typography sx={{ fontSize: "0.68rem", color: T.inkMuted }}>+{labels.length - max}</Typography>}
  </Stack>
);

export const Points = ({ value }) =>
  value === null || value === undefined ? null : (
    <Tooltip title="Story points">
      <Box component="span" sx={{ minWidth: 20, height: 20, px: 0.6, borderRadius: "10px", bgcolor: T.track, fontSize: "0.7rem", fontWeight: 700, color: T.inkSecondary, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{value}</Box>
    </Tooltip>
  );

export const Progress = ({ value, color = T.brand, height = 6, label }) => (
  <Box sx={{ minWidth: 60 }}>
    {label && (
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
        <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary }}>{label}</Typography>
        <Typography sx={{ fontSize: TYPE.micro, fontWeight: 700 }}>{Math.round(value || 0)}%</Typography>
      </Stack>
    )}
    <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, value || 0))} sx={{ height, borderRadius: 99, bgcolor: T.track, "& .MuiLinearProgress-bar": { bgcolor: color, borderRadius: 99 } }} />
  </Box>
);

/** Colour bar that is a project's identity (projects have no logo upload). */
export const ProjectBadge = ({ project, size = 32 }) => (
  <Box sx={{ width: size, height: size, borderRadius: "8px", bgcolor: project?.color || T.brand, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: size * 0.34, flexShrink: 0, letterSpacing: "-0.02em" }}>
    {(project?.key || "?").slice(0, 3)}
  </Box>
);

export const HEALTH = { green: { label: "On track", fg: "#067647", bg: "#DCFAE6" }, amber: { label: "At risk", fg: "#B54708", bg: "#FEF0C7" }, red: { label: "Off track", fg: "#B42318", bg: "#FEE4E2" } };

export const minutesText = (m) => {
  if (m === null || m === undefined) return "—";
  const w = Math.floor(m / 2400);
  const d = Math.floor((m % 2400) / 480);
  const h = Math.floor((m % 480) / 60);
  const min = m % 60;
  return [w && `${w}w`, d && `${d}d`, h && `${h}h`, min && `${min}m`].filter(Boolean).join(" ") || "0m";
};

export const has = (project, perm) => Boolean(project?.permissions?.includes(perm));

export const ACTIVITY_TEXT = (a, ctx) => {
  const name = a.actor?.name || "System";
  const d = a.meta?.display;
  const field = a.field;
  switch (a.action) {
    case "issue.created": return `${name} created ${a.issueKey}`;
    case "issue.updated":
      if (field === "description") return `${name} updated the description of ${a.issueKey}`;
      return `${name} changed ${field} of ${a.issueKey}${d ? ` to ${d}` : ""}`;
    case "issue.transitioned": return `${name} moved ${a.issueKey} from ${a.from} to ${a.to}`;
    case "issue.commented": return `${name} commented on ${a.issueKey}`;
    case "issue.deleted": return `${name} deleted ${a.issueKey}`;
    case "issue.attached": return `${name} attached ${(a.meta?.files || []).join(", ")} to ${a.issueKey}`;
    case "issue.linked": return `${name} linked ${a.issueKey}`;
    case "issue.moved": return `${name} moved ${a.from?.key || ""} to ${a.issueKey}`;
    case "worklog.added": return `${name} logged ${minutesText(a.to?.minutes)} on ${a.issueKey}`;
    case "sprint.started": return `${name} started ${a.to?.name}`;
    case "sprint.completed": return `${name} completed a sprint`;
    case "sprint.created": return `${name} created ${a.to?.name}`;
    case "release.released": return `${name} released ${a.to?.name}`;
    case "release.created": return `${name} created version ${a.to?.name}`;
    case "member.added": return `${name} added ${a.to?.name} as ${a.to?.role}`;
    case "member.removed": return `${name} removed ${a.from?.name}`;
    case "project.created": return `${name} created the project`;
    case "file.uploaded": return `${name} uploaded ${(a.meta?.files || []).join(", ")}`;
    case "workflow.changed": return `${name} changed the workflow`;
    case "automation.note": return a.meta?.text || "Automation note";
    default: return `${name} · ${String(a.action).replace(/[._]/g, " ")}${a.issueKey ? ` · ${a.issueKey}` : ""}`;
  }
};
